
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

export interface Conversation {
  id: string;
  listing_id: string;
  buyer_id: string;
  breeder_id: string;
  listing_title: string;
  listing_url: string;
  created_at: string;
  updated_at: string;
}

export const useConversations = () => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['conversations', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      // Get ALL conversations for the user (including empty ones)
      const { data, error } = await supabase
        .from('conversations')
        .select('*')
        .or(`buyer_id.eq.${user.id},breeder_id.eq.${user.id}`)
        .order('updated_at', { ascending: false });
      
      if (error) throw error;
      return data as Conversation[];
    },
    enabled: !!user,
  });
};

export const useCreateConversation = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async ({
      listing_id,
      breeder_id,
      listing_title,
      listing_url,
    }: {
      listing_id: string;
      breeder_id: string;
      listing_title: string;
      listing_url: string;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');
      
      const { data, error } = await supabase
        .from('conversations')
        .insert({
          listing_id,
          buyer_id: user.id,
          breeder_id,
          listing_title,
          listing_url,
        })
        .select()
        .single();
      
      if (error) {
        // If conversation already exists, fetch it
        if (error.code === '23505') {
          const { data: existing, error: fetchError } = await supabase
            .from('conversations')
            .select('*')
            .eq('listing_id', listing_id)
            .eq('buyer_id', user.id)
            .eq('breeder_id', breeder_id)
            .single();
          
          if (fetchError) throw fetchError;
          return existing;
        }
        throw error;
      }
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      toast({
        title: "Conversation Started",
        description: "You can now send messages.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to start conversation. Please try again.",
        variant: "destructive",
      });
    },
  });
};

// Add function to delete empty conversations
export const useDeleteEmptyConversation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (conversationId: string) => {
      // First check if conversation has any messages
      const { data: messages, error: messagesError } = await supabase
        .from('messages')
        .select('id')
        .eq('conversation_id', conversationId)
        .limit(1);
      
      if (messagesError) throw messagesError;
      
      // If no messages, delete the conversation
      if (messages.length === 0) {
        const { error } = await supabase
          .from('conversations')
          .delete()
          .eq('id', conversationId);
        
        if (error) throw error;
        return true;
      }
      
      return false;
    },
    onSuccess: (deleted) => {
      if (deleted) {
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
      }
    },
  });
};
